import React, { useEffect, useMemo, useState } from 'react';
import type { GamificationPeriod } from '@troia-v3/types';

import { ListPageEmpty, ListPageLoading, ListPagePagination } from '@/shared/components/ui';
import { currentPeriodKey } from '@/shared/utils/gamificationPeriod';

import { useGamificationEvents, useGamificationEventsSummary } from '../hooks/useGamification';

import { AuditFilters, type AuditFilterState } from './AuditFilters';
import { AuditKpis } from './AuditKpis';
import { AuditTable } from './AuditTable';

const PAGE_SIZE = 20;

/**
 * Aba "Auditoria de pontos" (mockup 10 B): 4 KPIs + filtros (Status/Evento/
 * Período) em popover + avatares de operador + chips removíveis + busca +
 * tabela do livro-caixa (`gamification-events`) + paginação.
 */
export const AuditTab: React.FC<{ period?: GamificationPeriod }> = ({ period = 'month' }) => {
  const [filters, setFilters] = useState<AuditFilterState>({ search: '' });
  const [page, setPage] = useState(1);
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebounced(filters.search.trim()), 300); return () => clearTimeout(t); }, [filters.search]);
  useEffect(() => { setPage(1); }, [filters.status, filters.ruleKey, filters.periodKey, filters.userId, debounced]);

  const current = useMemo(() => currentPeriodKey(period), [period]);
  const query = useMemo(() => ({
    page, limit: PAGE_SIZE,
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.ruleKey ? { ruleKey: filters.ruleKey } : {}),
    ...(filters.periodKey ? { periodKey: filters.periodKey } : {}),
    ...(filters.userId ? { userId: filters.userId } : {}),
    ...(debounced ? { search: debounced } : {}),
  }), [page, filters.status, filters.ruleKey, filters.periodKey, filters.userId, debounced]);

  const { data, isLoading, error } = useGamificationEvents(query);
  const { data: summary } = useGamificationEventsSummary(filters.periodKey);

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6" data-testid="gamification-audit-tab">
      <AuditKpis period={period} {...(summary ? { summary } : {})} />
      <AuditFilters value={filters} onChange={setFilters} period={period} currentPeriodKey={current} />
      {isLoading && !data && <ListPageLoading message="Carregando eventos..." />}
      {error && <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="gamification-audit-error">Erro ao carregar eventos: {error.message}</div>}
      {data && data.items.length === 0 && (
        <ListPageEmpty icon="list" message="Nenhum ponto registrado" description="Quando um lead for captado, um formulário preenchido ou uma venda fechada, a linha aparece aqui." />
      )}
      {data && data.items.length > 0 && (
        <>
          <AuditTable items={data.items} />
          <div className="mt-4" data-testid="gamification-audit-pagination">
            <ListPagePagination currentPage={data.page} totalPages={data.totalPages} onPageChange={setPage} totalItems={data.total} pageSize={PAGE_SIZE} itemLabel="eventos" />
          </div>
        </>
      )}
    </div>
  );
};
AuditTab.displayName = 'AuditTab';
